// frontend/src/pages/PartDetailPage.js
import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { partsApi } from '../api/parts';
import { settingsApi } from '../api/settings'; 
import { vehiclesApi } from '../api/vehicles'; 
import { aiApi } from '../api/ai'; 
import helpers from '../utils/helpers';
import { 
    FaShoppingCart, FaArrowLeft, FaCheck, FaTruck, FaShieldAlt, 
    FaMoneyBillWave, FaUndo, FaTimesCircle, FaWeightHanging, FaTag, FaBarcode, 
    FaStar, FaLock, FaPhone, FaWhatsapp, FaExclamationTriangle, FaEye, 
    FaRobot, FaSpinner, FaQuestionCircle, FaMagic, FaFlag, FaStore, FaHeadset, FaUserSlash, FaMapMarkerAlt, FaShippingFast,
    FaCheckCircle, FaRocket, FaEdit 
} from 'react-icons/fa';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext'; 
import SaveButton from '../components/common/SaveButton';
import ReviewSection from '../components/reviews/ReviewSection';
import DynamicMeta from '../components/common/DynamicMeta';
import ReportModal from '../components/common/ReportModal';
import BoostModal from '../components/common/BoostModal'; 
import PartCard from '../components/part/PartCard';

// --- CONFIGURATION CONSTANT ---
const EXCLUSIVE_STORE_ID = "everything_automotive_exclusive";
// ------------------------------

function PartDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate(); 
  const { addToCart } = useCart();
  const { user, isAuthenticated } = useAuth(); 
  
  const [part, setPart] = useState(null);
  const [relatedParts, setRelatedParts] = useState([]); 
  const [sellerParts, setSellerParts] = useState([]); 
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeImage, setActiveImage] = useState(null);
  
  // Contact State
  const [showContact, setShowContact] = useState(true);
  const [isPhoneRevealed, setIsPhoneRevealed] = useState(false);

  // Compatibility State
  const [garageVehicles, setGarageVehicles] = useState([]);
  const [isCompModalOpen, setIsCompModalOpen] = useState(false);
  const [compLoading, setCompLoading] = useState(false);
  const [compResult, setCompResult] = useState(null);
  const [selectedVehicleId, setSelectedVehicleId] = useState('');
  const [manualVehicle, setManualVehicle] = useState({ year: '', make: '', model: '' });
  const [useManualInput, setUseManualInput] = useState(false);

  // Modals
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [isBoostModalOpen, setIsBoostModalOpen] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch Part and Settings
        const [partData, settingsData] = await Promise.all([
            partsApi.getPartById(id),
            settingsApi.getAllSettings()
        ]);
        
        setPart(partData);
        
        // --- VISIBILITY LOGIC ---
        const siteContent = settingsData.site_content || {};
        const isExclusive = partData.store_id === EXCLUSIVE_STORE_ID;
        
        // Check the specific setting for parts
        // Default to TRUE if the setting is missing/undefined
        const isAllowed = siteContent.show_contact_parts !== false; 
        
        // Show if Exclusive Store OR if Setting is Enabled
        setShowContact(isExclusive || isAllowed);
        // ------------------------

        const initialImage = partData.images?.front_jpg || partData.itemurl || (partData.images && Object.values(partData.images)[0]);
        setActiveImage(initialImage);

        // Fetch Related Parts & Seller Parts
        try {
            const [related, sellerItems] = await Promise.all([
                partsApi.getRelatedParts(id),
                partsApi.getMoreFromSeller(id)
            ]);
            setRelatedParts(related);
            setSellerParts(sellerItems);
        } catch (e) {
            console.error("Failed to fetch additional parts", e);
        }

        // Fetch Garage if User is Logged In
        if (isAuthenticated) {
            try {
                const garage = await vehiclesApi.getGarage();
                setGarageVehicles(garage);
                if (garage.length > 0) {
                    setSelectedVehicleId(garage[0].id);
                } else {
                    setUseManualInput(true);
                }
            } catch (e) {
                console.error("Failed to load garage", e);
                setUseManualInput(true);
            }
        } else {
            setUseManualInput(true);
        }

      } catch (err) {
        console.error("Error fetching details:", err);
        setError("Failed to load part details.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, isAuthenticated]);

  // --- PRICE CALCULATION HELPER ---
  const getPriceDetails = () => {
      if (!part) return { original: 0, final: 0, hasDiscount: false };
      
      const original = parseFloat(part.price) || 0;
      const discount = parseFloat(part.discount_percentage) || 0;
      
      if (discount > 0) {
          const saved = original * (discount / 100);
          return { 
              original: original, 
              final: original - saved, 
              hasDiscount: true, 
              percent: discount 
          };
      }
      return { original: original, final: original, hasDiscount: false, percent: 0 };
  };

  const handleAddToCart = () => {
    if (part) {
      const { final } = getPriceDetails();
      addToCart({
          id: part.id,
          itemname: part.itemname,
          price: final, // Add discounted price
          image: part.images?.front_jpg || part.itemurl || (part.images && Object.values(part.images)[0]),
          type: 'part'
      });
      alert(`${part.itemname} added to cart!`);
    }
  };

  const handleCheckCompatibility = async (e) => {
      e.preventDefault();
      setCompLoading(true);
      setCompResult(null);

      try {
          const payload = {
              part_id: part.id,
              user_vehicle_id: useManualInput ? null : selectedVehicleId,
              manual_make: useManualInput ? manualVehicle.make : null,
              manual_model: useManualInput ? manualVehicle.model : null,
              manual_year: useManualInput ? parseInt(manualVehicle.year) : null
          };

          const result = await aiApi.checkCompatibility(payload);
          setCompResult(result);
      } catch (err) {
          alert("AI Check Failed. Please try again.");
      } finally {
          setCompLoading(false);
      }
  };

  const getImageLabel = (key) => {
      const labels = {
          front_jpg: 'Front View',
          back_jpg: 'Back View',
          label_jpg: 'Box Label',
          connector_jpg: 'Connector/Pins',
          stamp_jpg: 'Part Stamp',
          top_jpg: 'Top View',
          left_jpg: 'Left View',
          right_jpg: 'Right View'
      };
      return labels[key] || key;
  };

  const getFitmentColor = (status) => {
      switch(status) {
          case 'Direct Fit': return 'text-green-600 bg-green-50 border-green-200';
          case 'Likely Fit': return 'text-blue-600 bg-blue-50 border-blue-200';
          case 'Universal': return 'text-purple-600 bg-purple-50 border-purple-200';
          case 'Incompatible': return 'text-red-600 bg-red-50 border-red-200';
          default: return 'text-gray-600 bg-gray-50 border-gray-200';
      }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error || !part) {
    return (
      <div className="container mx-auto px-4 py-16 text-center">
        <h2 className="text-2xl font-bold text-red-600 mb-4">Error</h2>
        <p className="text-gray-700 mb-6">{error || "Part not found."}</p>
        <Link to="/parts" className="text-primary hover:underline flex items-center justify-center gap-2">
          <FaArrowLeft /> Back to Parts
        </Link>
      </div>
    );
  }

  const { original, final, hasDiscount, percent } = getPriceDetails();
  const partTitle = `${part.itemname} - ${helpers.formatCurrency(final, 'NGN')}`;
  const partDesc = `Buy ${part.itemname} for ${part.make} ${part.model}. Condition: ${part.condition}.`;

  // --- OWNER CHECK ---
  const isOwner = user && (user.id === part.user_id || user.roles.includes('admin') || user.roles.includes('super_admin'));
  const isPromoted = part.is_promoted && new Date(part.promoted_until) > new Date();

  // --- REUSABLE SIDEBAR CONTENT ---
  const SidebarContent = () => (
    <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
      
      {/* --- OWNER CONTROLS --- */}
      {isOwner && (
          <div className="mb-6 bg-blue-50 p-4 rounded-lg border border-blue-200">
              <h4 className="text-sm font-bold text-blue-800 mb-3 flex items-center gap-2">
                  <FaUserSlash /> Owner Controls
              </h4>
              <div className="grid grid-cols-2 gap-2">
                  <button 
                      onClick={() => setIsBoostModalOpen(true)}
                      className="bg-red-600 hover:bg-red-700 text-white text-xs font-bold py-2 px-3 rounded flex items-center justify-center gap-2 transition shadow-sm"
                  >
                      <FaRocket /> {isPromoted ? 'Extend Boost' : 'Boost Listing'}
                  </button>
                  <Link 
                      to="/account?tab=shop"
                      className="bg-white border border-blue-300 text-blue-700 text-xs font-bold py-2 px-3 rounded flex items-center justify-center gap-2 hover:bg-blue-50 transition"
                  >
                      <FaEdit /> Manage Item
                  </Link>
              </div>
              {isPromoted && (
                  <p className="text-xs text-red-600 font-bold mt-2 text-center">
                      🔥 Boost Active until {new Date(part.promoted_until).toLocaleDateString()}
                  </p>
              )}
          </div>
      )}

      <div className="mb-2">
        <span className="inline-block bg-gray-100 text-gray-600 text-xs px-2 py-1 rounded uppercase font-semibold tracking-wide">
          {part.condition}
        </span>
      </div>

      {/* TITLE & SAVE BUTTON */}
      <div className="flex justify-between items-start mb-2 gap-2">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 leading-tight break-words">
            {part.itemname}
        </h1>
        <div className="mt-1 flex-shrink-0">
            <SaveButton itemId={part.id} itemType="part" className="text-xl" />
        </div>
      </div>

      {/* Rating Display */}
      <div className="flex items-center gap-2 mb-4">
          <div className="flex text-yellow-400 text-sm">
              {[...Array(5)].map((_, i) => (
                  <FaStar key={i} className={i < Math.round(part.rating || 0) ? "text-yellow-400" : "text-gray-300"} />
              ))}
          </div>
          <span className="text-sm text-gray-500">({part.review_count || 0} reviews)</span>
      </div>

      {/* --- STORE NAME & LOCATION (LINKED) --- */}
      <div className="mb-4 pb-4 border-b border-gray-100">
          <div className="flex items-center gap-2 text-gray-700 font-bold mb-1">
              <FaStore className="text-blue-500" />
              {part.store_id ? (
                  <Link to={`/store/${part.store_id}`} className="hover:text-primary hover:underline">
                      {part.store_name || part.seller_name || 'Everything Automotive'}
                  </Link>
              ) : (
                  <span>{part.store_name || part.seller_name || 'Private Seller'}</span>
              )}
          </div>
          {part.location_lga && (
              <div className="flex items-center text-gray-500 text-sm ml-6">
                  <FaMapMarkerAlt className="mr-1 text-gray-400" />
                  {part.location_lga}, {part.location_state}
              </div>
          )}
      </div>

      {/* --- PRICE DISPLAY (UPDATED) --- */}
      <div className="mb-6">
          {hasDiscount ? (
              <div>
                  <div className="flex items-center gap-2 mb-1">
                      <span className="text-gray-400 line-through text-lg font-medium">
                          {helpers.formatCurrency(original, 'NGN')}
                      </span>
                      <span className="bg-red-100 text-red-600 text-xs font-bold px-2 py-1 rounded flex items-center gap-1">
                          <FaTag /> -{percent}% OFF
                      </span>
                  </div>
                  <div className="text-3xl md:text-4xl font-bold text-red-600">
                      {helpers.formatCurrency(final, 'NGN')}
                  </div>
              </div>
          ) : (
              <div className="text-3xl md:text-4xl font-bold text-primary">
                  {helpers.formatCurrency(final, 'NGN')}
              </div>
          )}
      </div>
      
      {/* --- EXPECTED DELIVERY DATE DISPLAY --- */}
      {part.expected_delivery_date && (
          <div className="bg-green-50 p-3 rounded-lg border border-green-200 mb-6">
              <div className="flex items-center gap-3 text-green-800">
                  <FaShippingFast className="text-xl flex-shrink-0" />
                  <div>
                      <span className="block text-xs font-bold uppercase">Expected Delivery</span>
                      <span className="font-bold text-sm">{part.expected_delivery_date}</span>
                  </div>
              </div>
          </div>
      )}

      {/* --- AI COMPATIBILITY CHECKER BUTTON --- */}
      <div className="mb-6">
          <button 
              onClick={() => setIsCompModalOpen(true)}
              className="w-full bg-purple-50 hover:bg-purple-100 text-purple-700 border border-purple-200 font-bold py-3 px-4 rounded-lg transition duration-300 flex items-center justify-center gap-2"
          >
              <FaRobot /> Check Compatibility with My Car
          </button>
          
          <div className="flex justify-between items-center mt-2 px-1">
              <p className="text-xs text-gray-500">
                  Powered by AI. Verify fitment before you buy.
              </p>
              <Link to="/garage-benefits" className="text-xs text-primary hover:underline flex items-center gap-1 font-bold">
                  <FaQuestionCircle /> Why use My Garage?
              </Link>
          </div>
      </div>

      {/* --- ACTION BUTTONS (BUY & CONTACT) --- */}
      <div className="space-y-3 mb-8">
          {/* 1. BUY BUTTON */}
          <button 
              onClick={handleAddToCart}
              className="w-full bg-primary hover:bg-primary-dark text-white font-bold py-4 px-6 rounded-lg shadow-lg transition duration-300 flex items-center justify-center gap-2 text-lg"
          >
              <FaShoppingCart /> Add to Cart
          </button>

          {/* 2. CONTACT BUTTONS */}
          {showContact && (
              part.store_phone ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {isPhoneRevealed ? (
                          <a href={`tel:${part.store_phone}`} className="bg-white border-2 border-green-600 text-green-600 font-bold py-3 rounded-lg flex items-center justify-center gap-2 hover:bg-green-50 transition">
                              <FaPhone /> {part.store_phone}
                          </a>
                      ) : (
                          <button 
                              onClick={() => setIsPhoneRevealed(true)}
                              className="bg-white border-2 border-green-600 text-green-600 font-bold py-3 rounded-lg flex items-center justify-center gap-2 hover:bg-green-50 transition"
                          >
                              <FaEye /> Show Phone
                          </button>
                      )}
                      
                      <a href={`https://wa.me/${part.store_phone.replace('+', '')}`} target="_blank" rel="noopener noreferrer" className="bg-green-600 text-white font-bold py-3 rounded-lg flex items-center justify-center gap-2 hover:bg-green-700 transition">
                          <FaWhatsapp className="text-xl" /> WhatsApp
                      </a>
                  </div>
              ) : (
                  // Fallback if no phone number
                  <div className="bg-gray-100 p-3 rounded-lg text-center border border-gray-200">
                      <p className="text-gray-500 text-sm font-medium flex items-center justify-center gap-2">
                          <FaUserSlash /> Seller Phone Not Listed
                      </p>
                      <Link to="/contact" className="text-xs text-primary hover:underline mt-1 block">
                          Contact Support for Help
                      </Link>
                  </div>
              )
          )}
      </div>

      {/* --- SAFETY WARNING --- */}
      {part.pay_on_delivery_status === 'Available' && (
          <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6 rounded-r-lg">
              <div className="flex items-start gap-3">
                  <FaExclamationTriangle className="text-red-500 text-xl mt-1 flex-shrink-0" />
                  <div>
                      <h4 className="font-bold text-red-800 text-sm">Safety Warning</h4>
                      <p className="text-sm text-red-700">
                          Do not pay this seller until you have received the item and verified it is exactly what you ordered.
                      </p>
                  </div>
              </div>
          </div>
      )}

      {/* --- POLICY TAGS --- */}
      <div className="mt-4">
          <h3 className="text-sm font-bold text-gray-500 uppercase mb-3">Policies</h3>
          <div className="bg-gray-50 rounded-lg p-4 text-sm space-y-3">
            {part.pay_on_delivery_status === 'Available' ? (
              <div className="flex items-center gap-3 text-green-700">
                <FaMoneyBillWave className="flex-shrink-0" />
                <strong>Pay on Delivery Available</strong>
              </div>
            ) : (
              <div className="flex items-center gap-3 text-red-700">
                <FaTimesCircle className="flex-shrink-0" />
                <span>Pay on Delivery Not Available</span>
              </div>
            )}

            {part.refundable_status === 'Refundable' ? (
              <div className="flex items-center gap-3 text-green-700">
                <FaUndo className="flex-shrink-0" />
                <strong>Item is Refundable</strong>
              </div>
            ) : (
              <div className="flex items-center gap-3 text-red-700">
                <FaTimesCircle className="flex-shrink-0" />
                <span>Item is Non-Refundable</span>
              </div>
            )}
          </div>
      </div>

      <p className="text-xs text-gray-400 text-center mt-4">
        Reference ID: {part.id}
      </p>

      {/* --- REPORT BUTTON --- */}
      <div className="mt-6 text-center border-t pt-4">
          <button 
              onClick={() => setIsReportModalOpen(true)}
              className="text-gray-400 hover:text-red-500 text-xs flex items-center justify-center gap-1 mx-auto transition-colors"
          >
              <FaFlag /> Report this listing
          </button>
      </div>
    </div>
  );

  return (
    <div className="bg-gray-50 min-h-screen py-8">
      <DynamicMeta 
          title={partTitle}
          description={partDesc}
          image={activeImage}
          url={`${window.location.origin}/parts/${id}`}
      />

      <div className="container mx-auto px-4">
        
        {/* --- STATUS WARNING BANNER --- */}
        {part.status && part.status !== 'active' && (
            <div className={`mb-6 p-4 rounded-lg border flex items-center gap-3 ${
                part.status === 'rejected' 
                ? 'bg-red-100 border-red-300 text-red-800' 
                : 'bg-yellow-100 border-yellow-300 text-yellow-800'
            }`}>
                {part.status === 'rejected' ? <FaTimesCircle className="text-xl" /> : <FaLock className="text-xl" />}
                <div>
                    <h3 className="font-bold text-lg">
                        {part.status === 'rejected' ? 'Listing Rejected' : 'Pending Review'}
                    </h3>
                    <p className="text-sm">
                        This part is <strong>not visible</strong> in the public shop search results. 
                        {part.status === 'pending_review' && " It is currently visible only to the seller and admins for preview."}
                    </p>
                </div>
            </div>
        )}

        <div className="mb-6">
          <Link to="/parts" className="text-gray-500 hover:text-primary flex items-center gap-2 text-sm">
            <FaArrowLeft /> Back to Inventory
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
          
          {/* LEFT: Images */}
          <div>
            <div className="bg-gray-100 rounded-lg overflow-hidden mb-4 h-64 md:h-96 flex items-center justify-center border border-gray-200 relative">
              <img 
                src={activeImage || 'https://via.placeholder.com/600x400?text=No+Image'} 
                alt={part.itemname} 
                className="max-h-full max-w-full object-contain"
              />
              {part.is_oem && (
                  <span className="absolute top-4 left-4 bg-blue-600 text-white text-xs font-bold px-3 py-1 rounded shadow-md">
                      GENUINE OEM
                  </span>
              )}
              {/* --- DISCOUNT BADGE ON IMAGE --- */}
              {hasDiscount && (
                  <div className="absolute top-4 right-4 bg-red-600 text-white px-4 py-2 rounded-full shadow-lg font-black text-lg z-10 animate-pulse">
                      {percent}% OFF
                  </div>
              )}
            </div>
            
            {part.images && Object.keys(part.images).length > 0 && (
              <div className="flex gap-2 overflow-x-auto pb-2">
                {Object.entries(part.images).map(([key, url]) => (
                  <button 
                    key={key}
                    onClick={() => setActiveImage(url)}
                    className={`relative w-20 h-20 md:w-24 md:h-24 flex-shrink-0 border-2 rounded-md overflow-hidden ${activeImage === url ? 'border-primary' : 'border-transparent'}`}
                  >
                    <img src={url} alt={key} className="w-full h-full object-cover" />
                    <span className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-60 text-white text-[8px] text-center py-0.5 truncate">
                        {getImageLabel(key)}
                    </span>
                  </button>
                ))}
              </div>
            )}

            {/* --- MOBILE ONLY: SIDEBAR CONTENT --- */}
            <div className="block lg:hidden mb-8">
                <SidebarContent />
            </div>

            {/* Technical Specs */}
            <div className="mt-8">
              <h3 className="text-lg font-semibold text-gray-800 mb-3">Technical Specifications</h3>
              <div className="bg-gray-50 rounded-lg p-4 text-sm">
                <div className="grid grid-cols-2 gap-y-3">
                  <div className="text-gray-500 flex items-center gap-2"><FaBarcode /> Part Number</div>
                  <div className="font-medium font-mono">{part.part_number || 'N/A'}</div>
                  
                  <div className="text-gray-500 flex items-center gap-2"><FaTag /> Type</div>
                  <div className="font-medium">
                      {part.is_oem ? <span className="text-blue-600 font-bold">Genuine OEM</span> : "Aftermarket"}
                  </div>
                  
                  <div className="text-gray-500 flex items-center gap-2"><FaWeightHanging /> Weight</div>
                  <div className="font-medium">{part.weight_kg ? `${part.weight_kg} kg` : 'N/A'}</div>
                  
                  <div className="text-gray-500">Category</div>
                  <div className="font-medium capitalize">{part.category_id?.replace(/_/g, ' ')}</div>
                  
                  <div className="text-gray-500">Brand</div>
                  <div className="font-medium capitalize">{part.brand_id?.replace(/_/g, ' ')}</div>
                  
                  <div className="text-gray-500">Description</div>
                  <div className="font-medium col-span-2 mt-1 text-gray-700 whitespace-pre-line text-justify">{part.description}</div>
                </div>
              </div>
            </div>

          </div>

          {/* RIGHT: Details (Desktop) */}
          <div className="hidden lg:block">
             <div className="sticky top-24">
                <SidebarContent />
             </div>
          </div>
        </div>

        {/* --- RELATED PRODUCTS SECTION --- */}
        {relatedParts.length > 0 && (
            <div className="mt-16 border-t border-gray-200 pt-10">
                <h3 className="text-2xl font-bold text-gray-800 mb-6">You Might Also Like</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">
                    {relatedParts.map(related => (
                        <PartCard key={related.id} part={related} />
                    ))}
                </div>
            </div>
        )}

        {/* --- MORE FROM SELLER SECTION --- */}
        {sellerParts.length > 0 && (
            <div className="mt-16 border-t border-gray-200 pt-10">
                <div className="flex items-center gap-2 mb-6">
                    <FaStore className="text-blue-600 text-2xl" />
                    <h3 className="text-2xl font-bold text-gray-800">
                        More from {part.store_name || 'This Seller'}
                    </h3>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">
                    {sellerParts.map(sellerItem => (
                        <PartCard key={sellerItem.id} part={sellerItem} />
                    ))}
                </div>
            </div>
        )}

        {/* --- REVIEWS SECTION --- */}
        <ReviewSection partId={id} />

      </div>

      {/* --- COMPATIBILITY MODAL --- */}
      {isCompModalOpen && (
          <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-60 p-4">
              <div className="bg-white rounded-lg shadow-2xl w-full max-w-md animate-fadeInUp">
                  <div className="flex justify-between items-center p-4 border-b bg-purple-50 rounded-t-lg">
                      <h3 className="font-bold text-purple-800 flex items-center gap-2">
                          <FaRobot /> AI Compatibility Check
                      </h3>
                      <button onClick={() => setIsCompModalOpen(false)} className="text-gray-500 hover:text-gray-700">
                          <FaTimesCircle size={20} />
                      </button>
                  </div>
                  
                  <div className="p-6">
                      {!compResult ? (
                          <form onSubmit={handleCheckCompatibility} className="space-y-4">
                              <p className="text-sm text-gray-600 mb-4">
                                  Select a vehicle from your garage or enter details manually to check if this part fits.
                              </p>

                              {/* Garage Selection */}
                              {garageVehicles.length > 0 && (
                                  <div className="mb-4">
                                      <label className="flex items-center gap-2 cursor-pointer mb-2">
                                          <input 
                                              type="radio" 
                                              checked={!useManualInput} 
                                              onChange={() => setUseManualInput(false)}
                                              className="text-purple-600"
                                          />
                                          <span className="font-bold text-gray-700">Select from Garage</span>
                                      </label>
                                      <select 
                                          className="w-full p-2 border rounded bg-white disabled:bg-gray-100"
                                          disabled={useManualInput}
                                          value={selectedVehicleId}
                                          onChange={(e) => setSelectedVehicleId(e.target.value)}
                                      >
                                          {garageVehicles.map(v => (
                                              <option key={v.id} value={v.id}>
                                                  {v.year} {v.make} {v.model}
                                              </option>
                                          ))}
                                      </select>
                                  </div>
                              )}

                              {/* Manual Input */}
                              <div>
                                  <label className="flex items-center gap-2 cursor-pointer mb-2">
                                      <input 
                                          type="radio" 
                                          checked={useManualInput} 
                                          onChange={() => setUseManualInput(true)}
                                          className="text-purple-600"
                                      />
                                      <span className="font-bold text-gray-700">Enter Manually</span>
                                  </label>
                                  <div className={`space-y-2 ${!useManualInput ? 'opacity-50 pointer-events-none' : ''}`}>
                                      <input 
                                          placeholder="Year (e.g. 2015)" 
                                          type="number"
                                          className="w-full p-2 border rounded"
                                          value={manualVehicle.year}
                                          onChange={e => setManualVehicle({...manualVehicle, year: e.target.value})}
                                      />
                                      <input 
                                          placeholder="Make (e.g. Toyota)" 
                                          className="w-full p-2 border rounded"
                                          value={manualVehicle.make}
                                          onChange={e => setManualVehicle({...manualVehicle, make: e.target.value})}
                                      />
                                      <input 
                                          placeholder="Model (e.g. Camry)" 
                                          className="w-full p-2 border rounded"
                                          value={manualVehicle.model}
                                          onChange={e => setManualVehicle({...manualVehicle, model: e.target.value})}
                                      />
                                  </div>
                              </div>

                              <button 
                                  type="submit" 
                                  disabled={compLoading}
                                  className="w-full bg-purple-600 text-white font-bold py-3 rounded-lg hover:bg-purple-700 flex items-center justify-center gap-2 mt-4"
                              >
                                  {compLoading ? <FaSpinner className="animate-spin" /> : <FaMagic />} Run AI Check
                              </button>
                          </form>
                      ) : (
                          <div className="animate-fadeIn">
                              <div className={`p-4 rounded-lg border mb-4 text-center ${getFitmentColor(compResult.fitment_status)}`}>
                                  <div className="text-3xl mb-2">
                                      {compResult.fitment_status === 'Incompatible' ? <FaTimesCircle /> : 
                                       compResult.fitment_status === 'Unknown' ? <FaQuestionCircle /> : <FaCheckCircle />}
                                  </div>
                                  <h3 className="text-xl font-bold uppercase">{compResult.fitment_status}</h3>
                                  <p className="text-sm font-bold mt-1">Confidence: {compResult.confidence_score}%</p>
                              </div>

                              <div className="bg-gray-50 p-4 rounded border border-gray-200 mb-4">
                                  <h4 className="font-bold text-gray-700 text-sm mb-2">AI Analysis:</h4>
                                  <p className="text-sm text-gray-600">{compResult.reasoning}</p>
                              </div>

                              <button 
                                  onClick={() => setCompResult(null)}
                                  className="w-full bg-gray-200 text-gray-700 font-bold py-2 rounded hover:bg-gray-300"
                              >
                                  Check Another Vehicle
                              </button>
                          </div>
                      )}
                  </div>
              </div>
          </div>
      )}

      {/* --- REPORT MODAL --- */}
      <ReportModal 
          isOpen={isReportModalOpen} 
          onClose={() => setIsReportModalOpen(false)} 
          itemId={id}
          itemName={part.itemname}
          itemType="part"
      />

      {/* --- BOOST MODAL --- */}
      {isBoostModalOpen && (
          <BoostModal 
              isOpen={isBoostModalOpen}
              onClose={() => setIsBoostModalOpen(false)}
              itemId={part.id}
              itemType="part"
              itemName={part.itemname}
          />
      )}

    </div>
  );
}

export default PartDetailPage;